import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Layout, Button, Modal } from "antd";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import {
  FaArrowRight,
  FaCheckCircle,
  FaMedal,
  FaRegClock,
} from "react-icons/fa";
import { GiHamburgerMenu } from "react-icons/gi";
import { ImageUrl } from "../../helpers/Functions";
import "./b2b-training.css";

function Homepage() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);
  const [modal2Open, setModal2Open] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (modal2Open && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.log("Autoplay failed:", error);
      });
    }
  }, [modal2Open]);

  React.useEffect(() => {
    if (!user && !token) {
      navigate("/", { replace: true });
    }
  }, [user, token, navigate]);

  const data1 = [
    { title: "Log in with company credentials using Single Sign-on." },
    {
      title:
        "Notify external systems when employees complete training courses.",
    },
    {
      title: "Share badges & certificates with LinkedIn and Accredible.",
    },
  ];

  const data3 = [
    {
      Icon: GiHamburgerMenu,
      title: "Track new hires progress",
      des: "Accurately track the progress of new hires in an onboarding program.",
    },
    {
      Icon: FaRegClock,
      title: "Automate HR",
      des: "Automate HR paperwork and reduce new-hire expenses.",
    },
    {
      Icon: FaMedal,
      title: "Guide employees",
      des: "Guide employees into professional development after their onboarding.",
    },
  ];

  const data5 = [
    {
      title: "Test knowledge retention with interactive assignments.",
    },
    {
      title: "Employees can train when they’re available & ready to learn.",
    },
  ];

  const departments = [
    "Management Team",
    "Social Services",
    "Education",
    "Facilities",
    "Nutrition",
    "Parent Training",
    "Board Development",
  ];

  return (
    <>
      <Helmet>
        <title>
          Professional Development & Safety Workshops for Educators
        </title>
        <meta
          name="description"
          content="Advance your early education team with professional development programs, health and safety workshops designed to enhance skills and knowledge."
        />
        <meta
          property="og:title"
          content="Professional Development & Safety Workshops for Educators"
        />
        <meta
          property="og:description"
          content="Advance your early education team with professional development programs, health and safety workshops designed to enhance skills and knowledge."
        />
      </Helmet>

      <Layout className="b2b-page">
        <section
          className="b2b-hero"
          style={{ backgroundImage: `url(${ImageUrl("new-78.png")})` }}
        >
          <div className="b2b-hero-overlay" aria-hidden />
          <div className="b2b-wrap b2b-hero-inner">
            <div className="b2b-hero-copy">
              <span className="b2b-hero-badge">
                LMS FOR EMPLOYEE DEVELOPMENT & ONBOARDING
              </span>
              <h1 className="fontFamily1 b2b-hero-title">
                Maintain & Grow Teams With <span>Employee Training</span>
              </h1>
              <p className="fontFamily1 b2b-hero-sub">
                Help employees learn new skills, earn certifications, and
                maintain professional knowledge with an all-inclusive learning
                platform. Create courses and online workshops, then track
                employee performance with one system.
              </p>
              <div className="b2b-hero-actions">
                {!token && (
                  <Button
                    type="primary"
                    className="b2b-hero-btn-primary"
                    onClick={() => navigate("/signup")}
                  >
                    Register Now
                  </Button>
                )}
                <Button
                  className="b2b-hero-btn-outline"
                  onClick={() => setModal2Open(true)}
                >
                  See The Platform <FaArrowRight />
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--white">
          <div className="b2b-wrap">
            <div className="b2b-split">
              <div className="b2b-split-media">
                <img src={ImageUrl("new-58.png")} alt="" loading="lazy" />
              </div>
              <div className="b2b-split-copy">
                <img
                  src={ImageUrl("miniImg2.png")}
                  alt=""
                  className="b2b-section-kicker"
                />
                <h2 className="fontFamily1">
                  Employee Training That Can Be Taken Any Place— Any Time
                </h2>
                <p>
                  Create training courses that employees can take from anywhere
                  in the world, at any time. Courses can be made from videos,
                  docs, pdfs, text modules, SCORM, and more.
                </p>
                <ul className="b2b-check-list">
                  {data5.map((item) => (
                    <li key={item.title}>
                      <FaCheckCircle aria-hidden />
                      <span>{item.title}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="b2b-btn-ghost"
                  onClick={() => navigate("/pricingPlans")}
                >
                  See Pricing
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--muted">
          <div className="b2b-wrap">
            <div className="b2b-section-intro">
              <span className="b2b-section-kicker">
                <img src={ImageUrl("miniImg2.png")} alt="" />
              </span>
              <h2 className="fontFamily1 b2b-section-title">
                Reporting On Training Has Never Been Easier
              </h2>
              <p className="fontFamily1 b2b-section-lead">
                Automatically track the progress of employees in a training
                program. From completions and certifications to quiz scores and
                attendance records — keep track of everything happening in
                employee training.
              </p>
            </div>

            <div className="b2b-cards">
              <article className="b2b-card">
                <div className="b2b-card-media">
                  <img src={ImageUrl("new-79.png")} alt="" loading="lazy" />
                </div>
                <div className="b2b-card-body">
                  <h3>Manage Certifications and Training Attendance</h3>
                  <p>
                    Manage employee training with a platform that keeps track of
                    who is certified and up to date on training.
                  </p>
                </div>
              </article>
              <article className="b2b-card">
                <div className="b2b-card-media">
                  <img src={ImageUrl("new-80.png")} alt="" loading="lazy" />
                </div>
                <div className="b2b-card-body">
                  <h3>Monitor Assignment or Quiz Completions and Scores</h3>
                  <p>
                    Monitor how employees are performing in online training using
                    software with built-in learner reports.
                  </p>
                </div>
              </article>
              <article className="b2b-card">
                <div className="b2b-card-media">
                  <img src={ImageUrl("new-81.png")} alt="" loading="lazy" />
                </div>
                <div className="b2b-card-body">
                  <h3>Track How Long Employees Are Training</h3>
                  <p>
                    Track how much time employees spend in training programs
                    using software that tracks user-activities.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--white">
          <div className="b2b-wrap">
            <div className="b2b-split">
              <div className="b2b-split-copy">
                <img
                  src={ImageUrl("miniImg4.png")}
                  alt=""
                  className="b2b-section-kicker"
                />
                <h2 className="fontFamily1">
                  Eliminate Inefficiencies In Professional Development
                </h2>
                <p>
                  Connect with 3rd-party tools to create high-quality employees
                  training that’s convenient for your company to manage.
                </p>
                <ul className="b2b-check-list">
                  {data1.map((item) => (
                    <li key={item.title}>
                      <FaCheckCircle aria-hidden />
                      <span>{item.title}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="b2b-btn-ghost"
                  onClick={() => navigate("/pricingPlans")}
                >
                  See Pricing
                </Button>
              </div>
              <div className="b2b-split-media">
                <img src={ImageUrl("new-55.png")} alt="" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--navy">
          <div className="b2b-wrap">
            <div className="b2b-section-intro">
              <span className="b2b-section-kicker">
                <img src={ImageUrl("miniImg2.png")} alt="" />
              </span>
              <h2 className="fontFamily1 b2b-section-title">
                Fast-Track New Hire Onboarding
              </h2>
              <p className="fontFamily1 b2b-section-lead">
                Create an onboarding program that makes hiring new talent
                efficient and scalable.
              </p>
              {!token && (
                <div className="b2b-cta-center">
                  <Button
                    className="b2b-btn-light"
                    onClick={() => navigate("/signup")}
                  >
                    Get Started
                  </Button>
                </div>
              )}
            </div>

            <div className="b2b-split">
              <div className="b2b-split-media">
                <img src={ImageUrl("new-82.png")} alt="" loading="lazy" />
              </div>
              <div className="b2b-split-copy">
                <ul className="b2b-feature-list">
                  {data3.map((item) => {
                    const Icon = item.Icon;
                    return (
                      <li key={item.title}>
                        <span className="b2b-feature-icon" aria-hidden>
                          <Icon />
                        </span>
                        <div>
                          <h4>{item.title}</h4>
                          <p>{item.des}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--muted">
          <div className="b2b-wrap">
            <div className="b2b-split">
              <div className="b2b-split-media">
                <img src={ImageUrl("new-83.png")} alt="" loading="lazy" />
              </div>
              <div className="b2b-split-copy">
                <img
                  src={ImageUrl("miniImg4.png")}
                  alt=""
                  className="b2b-section-kicker"
                />
                <h2 className="fontFamily1">Separate Training By Department</h2>
                <p>
                  Use portals to train all of your teams online without having
                  one department’s training overlap with the next.
                </p>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    marginBottom: 18,
                  }}
                >
                  {departments.map((dept) => (
                    <Button key={dept} className="b2b-btn-ghost">
                      {dept}
                    </Button>
                  ))}
                </div>
                {!token && (
                  <Button
                    className="b2b-hero-btn-primary"
                    onClick={() => navigate("/signup")}
                  >
                    Get Started
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--white">
          <div className="b2b-wrap">
            <div className="b2b-section-intro">
              <span className="b2b-section-kicker">
                <img src={ImageUrl("miniImg2.png")} alt="" />
              </span>
              <h2 className="fontFamily1 b2b-section-title">
                Test Your Employee&apos;s Understanding & Skills
              </h2>
              <p className="fontFamily1 b2b-section-lead">
                Attach quizzes, written assignments, and surveys in a course to
                gauge how well employees understand training material.
              </p>
            </div>

            <div className="b2b-cards">
              <article className="b2b-card">
                <div className="b2b-card-media">
                  <img src={ImageUrl("new-84.png")} alt="" loading="lazy" />
                </div>
                <div className="b2b-card-body">
                  <h3>Create Quizzes & Tests</h3>
                  <p>
                    Create multiple-choice, multiple-correct, and other style
                    quizzes.
                  </p>
                </div>
              </article>
              <article className="b2b-card">
                <div className="b2b-card-media">
                  <img src={ImageUrl("new-85.png")} alt="" loading="lazy" />
                </div>
                <div className="b2b-card-body">
                  <h3>Give Employees Assignments</h3>
                  <p>
                    Test employees’ critical thinking with assignments.
                  </p>
                </div>
              </article>
              <article className="b2b-card">
                <div className="b2b-card-media">
                  <img src={ImageUrl("new-86.png")} alt="" loading="lazy" />
                </div>
                <div className="b2b-card-body">
                  <h3>Get Company Feedback</h3>
                  <p>
                    Get feedback from employees using different surveys types.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--navy">
          <div className="b2b-wrap">
            <div className="b2b-split">
              <div className="b2b-split-copy">
                <img
                  src={ImageUrl("miniImg2.png")}
                  alt=""
                  className="b2b-section-kicker"
                />
                <h2 className="fontFamily1">Improve Company Culture</h2>
                <p>
                  Use WrightCo Academy to improve your organization’s culture
                  during training. Create team assignments that foster
                  collaboration, and train groups of employees face-to-face and
                  online, using virtual instructor-led training.
                </p>
                {!token && (
                  <Button
                    className="b2b-btn-light"
                    onClick={() => navigate("/signup")}
                  >
                    Get Started
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--white">
          <div className="b2b-wrap">
            <div className="b2b-section-intro">
              <span className="b2b-section-kicker">
                <img src={ImageUrl("miniImg2.png")} alt="" />
              </span>
              <h2 className="fontFamily1 b2b-section-title">
                Your Organization&apos;s Success Is Priority
              </h2>
              <img
                src={ImageUrl("new-88.png")}
                alt=""
                style={{ maxWidth: 150, margin: "12px auto 0", display: "block" }}
              />
            </div>

            <div className="b2b-split" style={{ alignItems: "start" }}>
              <div className="b2b-split-media" style={{ maxWidth: 180 }}>
                <img src={ImageUrl("new-87.png")} alt="" loading="lazy" />
              </div>
              <div className="b2b-split-copy">
                <p>
                  “We love the customization that WrightCo offers. We maintain
                  very high brand standards and through their brilliant team, we
                  were able to make our vision come to life. With WrightCo
                  fantastic team we have built a sophisticated platform.
                  WrightCo has proven to be professional, accommodating and
                  advanced from the beginning.”
                </p>
                <h2
                  className="fontFamily1"
                  style={{ fontSize: 18, marginBottom: 4 }}
                >
                  Rebecca P.
                </h2>
                <p style={{ color: "#00b8ae", fontWeight: 700, margin: 0 }}>
                  Ultimate Reality Inc.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-trainer">
          <div className="b2b-wrap">
            <div className="b2b-trainer-card">
              <h3>We&apos;re Looking for Trainers</h3>
              <h3>Teach. Create. Earn.</h3>
              <p>
                Are you passionate about teaching? Ready to share your expertise,
                connect with professionals, and make a meaningful impact, all
                while earning extra income?
              </p>

              <h4>You&apos;ve come to the right place.</h4>
              <p>
                At WrightCo Academy, we are building a network of world-class
                trainers and industry experts dedicated to developing
                high-quality courseware for professionals in early care and
                education. If you&apos;re an expert in your field with a desire
                to teach, we&apos;d love to have you on board!
              </p>

              <h3>Why Join Us?</h3>
              <ul>
                <li>
                  <p>
                    ✅ Expand Your Reach – Share your knowledge with a broad
                    audience of professionals.
                  </p>
                </li>
                <li>
                  <p>
                    ✅ Boost Your Visibility – Gain recognition as a trusted
                    expert in your industry.
                  </p>
                </li>
                <li>
                  <p>
                    ✅ Join a Thriving Community – Connect with like-minded
                    trainers and industry leaders.
                  </p>
                </li>
                <li>
                  <p>
                    ✅ Earn Passive Income – Our fair and flexible revenue-sharing
                    model ensures unlimited earning potential.
                  </p>
                </li>
              </ul>

              <h3>What It Takes to Be a WrightCo Trainer</h3>
              <ul>
                <li>
                  <p>
                    ✔ <strong>Passion for Teaching</strong> – A commitment to
                    sharing knowledge and empowering others.
                  </p>
                </li>
                <li>
                  <p>
                    ✔ <strong>Industry Expertise</strong> – Relevant
                    certifications and experience in your domain.
                  </p>
                </li>
                <li>
                  <p>
                    ✔ <strong>Strong Communication Skills</strong> – Ability to
                    present information effectively.
                  </p>
                </li>
                <li>
                  <p>
                    ✔ <strong>Commitment to Excellence</strong> – Willingness to
                    develop and deliver high-quality training.
                  </p>
                </li>
              </ul>
              <h4>Ready to Make an Impact?</h4>
              <p>
                Join WrightCo Academy today and take the next step in your
                teaching journey.
              </p>
            </div>
          </div>
        </section>

        <Modal
          className="b2b-video-modal"
          centered
          open={modal2Open}
          onOk={() => setModal2Open(false)}
          onCancel={() => setModal2Open(false)}
          width={1000}
          footer={false}
        >
          <div style={{ padding: "24px 12px" }}>
            <video width="auto" height="auto" ref={videoRef} controls>
              <source src="https://react.customdev.solutions:3012/Uploads/tutorVideo.mp4" />
              <source src="movie.ogg" type="video/ogg" />
            </video>
          </div>
        </Modal>
      </Layout>
    </>
  );
}

export default Homepage;
